import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ALIGNMENT_AXES, alignmentBand } from '@mlm/shared/coaching';
import { cn } from '@mlm/ui/lib/cn';
import type { Today } from '../../lib/api';
import { readingHeadline } from '../../lib/home';
import { AxesRing, AxisDot, BrandMark } from '../visual';

const REFLECTIVE = 'Guidance scores are reflective indices, not predictions.';

const darkEyebrow =
  'font-mono text-[0.62rem] font-medium uppercase tracking-[0.16em] text-paper/60';
const pill =
  'inline-flex min-h-11 items-center justify-center rounded-full px-5 font-mono text-[0.62rem] uppercase tracking-[0.14em] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A]';
const goldPill = cn(pill, 'bg-gold text-ink hover:bg-[#c29d4c]');
const ghostPill = cn(pill, 'border border-paper/25 text-paper hover:border-paper/50');

/** What Master Kala makes of the day: the daily engine's insight, in a glass panel. */
function KalaNote({ insight, layout }: { insight: string; layout: 'phone' | 'desktop' }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-2xl border border-paper/10 bg-paper/[0.06]',
        layout === 'phone'
          ? 'mt-3.5 px-3.5 py-3'
          : 'mt-4 max-w-[560px] gap-3.5 rounded-[18px] px-4 py-3.5',
      )}
    >
      <BrandMark size={layout === 'phone' ? 30 : 34} onDark />
      <div className="min-w-0">
        <p className="font-mono text-[0.56rem] uppercase tracking-[0.14em] text-paper/55">
          Master Kala says
        </p>
        <p
          className={cn(
            'mt-1 leading-relaxed text-paper/90',
            layout === 'phone' ? 'text-[13.5px]' : 'text-[14.5px]',
          )}
        >
          {insight}
        </p>
      </div>
    </div>
  );
}

/** The four axes as a list beside the ring: dot, name, score, band. */
function AxesList({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const a = today.alignment!;
  return (
    <ul className={cn('flex min-w-0 flex-1 flex-col', layout === 'phone' ? 'gap-2' : 'gap-2.5')}>
      {ALIGNMENT_AXES.map(({ key, label }, i) => (
        <li
          key={key}
          className={cn('flex items-center', layout === 'phone' ? 'gap-1.5' : 'gap-2.5')}
        >
          <AxisDot index={i} />
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-paper/90',
              layout === 'phone' ? 'text-[13px]' : 'text-sm',
            )}
          >
            {label}
          </span>
          <span className={cn('font-mono', layout === 'phone' ? 'text-xs' : 'text-[13px]')}>
            {a[key]}
          </span>
          <span
            className={cn(
              'font-mono uppercase tracking-[0.12em] text-paper/50',
              layout === 'phone'
                ? 'hidden w-[60px] text-[0.5rem] min-[380px]:block'
                : 'w-[76px] text-[0.52rem]',
            )}
          >
            {alignmentBand(a[key])}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Today's reading (SC-13's hero in the redesign): the day's alignment as a
 * ring, Master Kala's note and the way into the full Vedic guidance. Without
 * birth details it asks for them instead of showing numbers it does not have.
 *
 * It never reads the guidance itself: opening the guidance records the day's
 * alignment practice, and that must stay the member's own act.
 */
export function ReadingCard({ today, layout }: { today: Today; layout: 'phone' | 'desktop' }) {
  const a = today.alignment;
  const needsBirth = !a && !today.profile.hasBirthDetails;
  const headline = a ? readingHeadline(a) : needsBirth ? 'Add your birth details' : null;
  const cta = needsBirth ? (
    <Link to="/settings" className={cn(goldPill, layout === 'phone' && 'flex-1')}>
      Add birth details
    </Link>
  ) : (
    <Link to="/align/vedic" className={cn(goldPill, layout === 'phone' && 'flex-1')}>
      Full Vedic guidance
    </Link>
  );

  const glows = (
    <>
      <div
        aria-hidden
        className={cn(
          'glow-gold pointer-events-none absolute rounded-full',
          layout === 'phone'
            ? '-right-[70px] -top-[70px] h-60 w-60'
            : '-right-[90px] -top-[110px] h-[360px] w-[360px]',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'glow-verde pointer-events-none absolute rounded-full',
          layout === 'phone'
            ? '-bottom-[90px] -left-[60px] h-[220px] w-[220px]'
            : '-bottom-[140px] -left-20 h-80 w-80',
        )}
      />
    </>
  );

  if (layout === 'phone') {
    return (
      <section className="relative mx-5 mt-[18px] overflow-hidden rounded-3xl bg-reading px-[18px] pb-4 pt-[18px] text-paper shadow-[0_18px_40px_-22px_rgba(16,32,26,0.6)]">
        {glows}
        <div className="relative">
          <p className={darkEyebrow}>Today's reading</p>
          {headline && (
            <h2 className="mt-2.5 text-pretty font-display text-[23px] font-medium leading-[1.2]">
              {headline}
            </h2>
          )}
          {a ? (
            <div className="mt-3.5 flex items-center gap-3">
              <AxesRing
                alignment={a}
                size={112}
                caption={alignmentBand(a.overall)}
                numberClass="text-[34px]"
              />
              <AxesList today={today} layout="phone" />
            </div>
          ) : needsBirth ? (
            <p className="mt-2 text-[13.5px] leading-relaxed text-paper/75">
              Your birth chart personalizes the daily Vedic guidance.
            </p>
          ) : null}
          {today.insight && <KalaNote insight={today.insight} layout="phone" />}
          <div className="mt-3 flex gap-2">
            {cta}
            <Link to="/coach" className={cn(ghostPill, 'flex-1')}>
              Ask Master Kala
            </Link>
          </div>
          {a && <p className="mt-3 text-[10.5px] leading-normal text-paper/40">{REFLECTIVE}</p>}
        </div>
      </section>
    );
  }

  return (
    <section className="relative mt-[22px] grid items-center gap-7 overflow-hidden rounded-3xl bg-reading px-[26px] py-6 text-paper shadow-[0_18px_40px_-22px_rgba(16,32,26,0.6)] xl:grid-cols-[minmax(0,1fr)_440px]">
      {glows}
      <div className="relative min-w-0">
        <p className={darkEyebrow}>Today's reading</p>
        {headline && (
          <h2 className="mt-2.5 max-w-[22ch] text-pretty font-display text-[30px] font-medium leading-[1.15]">
            {headline}
          </h2>
        )}
        {needsBirth && (
          <p className="mt-2 text-[14.5px] leading-relaxed text-paper/75">
            Your birth chart personalizes the daily Vedic guidance.
          </p>
        )}
        {today.insight && <KalaNote insight={today.insight} layout="desktop" />}
        <div className="mt-4 flex flex-wrap items-center gap-2.5">
          {cta}
          {!needsBirth && (
            <Link to="/align/chart" className={ghostPill}>
              Your birth chart
            </Link>
          )}
          <Link
            to="/coach"
            className="flex min-h-11 w-[300px] max-w-full items-center justify-between gap-4 rounded-full border border-paper/[0.18] bg-paper/[0.06] pl-4 pr-[5px] text-sm text-paper/60 transition-colors hover:border-paper/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E6C87A]"
          >
            <span>Ask Master Kala anything…</span>
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-gold text-ink">
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden />
            </span>
          </Link>
        </div>
      </div>
      {a && (
        <div className="relative flex max-w-[520px] items-center gap-[18px] xl:max-w-none">
          <AxesRing
            alignment={a}
            size={160}
            caption={`${alignmentBand(a.overall)} day`}
            numberClass="text-[44px]"
          />
          <div className="min-w-0 flex-1">
            <AxesList today={today} layout="desktop" />
            <p className="mt-3.5 text-[11px] leading-normal text-paper/[0.42]">{REFLECTIVE}</p>
          </div>
        </div>
      )}
    </section>
  );
}
